import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RefreshCw, Loader2, CheckCircle, XCircle, AlertTriangle, Image } from 'lucide-react';
import { getDdfStatus, triggerDdfSync } from '../lib/api';
import OpsLayout from '../components/OpsLayout';

export default function AdminDdfMonitor() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/ops/login'); return; }
    loadStatus();
  }, [navigate]);

  const loadStatus = () => {
    setLoading(true);
    getDdfStatus().then(r => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerDdfSync();
      // Poll for completion
      setTimeout(() => { loadStatus(); setSyncing(false); }, 30000);
    } catch (e) { alert('Sync failed'); setSyncing(false); }
  };

  if (loading) return <OpsLayout><div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></OpsLayout>;

  const s = status || {};
  const photoRate = s.active > 0 ? Math.round((s.with_photos / s.active) * 100) : 0;
  const coverageCount = s.by_neighbourhood?.length || 0;

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="ddf-monitor">
      <div className="bg-white border-b border-slate-200 px-6 md:px-10 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>MLS Listings Monitor</h1>
            <p className="text-xs text-slate-400 mt-0.5">CREA DDF sync status, photo quality, and neighbourhood coverage</p>
          </div>
          <Button onClick={handleSync} disabled={syncing} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-9 px-5 text-xs font-medium gap-1.5">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      <div className="px-6 md:px-10 py-8 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Active</p>
            <p className="font-mono text-3xl font-bold text-emerald-700 mt-1">{s.active || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Removed</p>
            <p className="font-mono text-3xl font-bold text-slate-400 mt-1">{s.removed || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Photo Rate</p>
            <p className={`font-mono text-3xl font-bold mt-1 ${photoRate >= 90 ? 'text-emerald-700' : photoRate >= 70 ? 'text-amber-600' : 'text-red-500'}`}>{photoRate}%</p>
            <p className="text-[10px] text-slate-400">{s.with_photos || 0} with / {s.no_photos || 0} without</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Coverage</p>
            <p className={`font-mono text-3xl font-bold mt-1 ${coverageCount >= 10 ? 'text-emerald-700' : coverageCount >= 6 ? 'text-amber-600' : 'text-red-500'}`}>{coverageCount}/12</p>
            <p className="text-[10px] text-slate-400">neighbourhoods</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Last Sync</p>
            <p className="text-sm font-mono text-slate-700 mt-1">{s.last_synced ? new Date(s.last_synced).toLocaleTimeString() : 'Never'}</p>
            <p className="text-[10px] text-slate-400">{s.last_synced ? new Date(s.last_synced).toLocaleDateString() : ''}</p>
          </div>
        </div>

        {/* Neighbourhood Breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">Neighbourhood Breakdown</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-medium">Neighbourhood</TableHead>
                <TableHead className="text-xs font-medium text-right">Listings</TableHead>
                <TableHead className="text-xs font-medium text-right">Avg Price</TableHead>
                <TableHead className="text-xs font-medium">Coverage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {['aldershot', 'alton-village', 'burlington-east', 'downtown-burlington', 'freeman', 'kilbride', 'lasalle', 'millcroft', 'orchard', 'roseland', 'shoreacres', 'tyandaga'].map(slug => {
                const data = s.by_neighbourhood?.find(n => n.neighbourhood === slug);
                const count = data?.count || 0;
                return (
                  <TableRow key={slug}>
                    <TableCell className="text-sm font-medium capitalize">{slug.replace(/-/g, ' ')}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{count}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-500">{data ? `$${data.avg_price.toLocaleString()}` : '—'}</TableCell>
                    <TableCell>
                      {count > 0 ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-slate-300" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Type Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['detached', 'townhome', 'condo', 'other'].map(type => (
            <div key={type} className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 capitalize">{type}</p>
              <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{s.by_type?.[type] || 0}</p>
            </div>
          ))}
        </div>

        {/* Sync History */}
        {s.sync_history?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Sync History</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-medium">Time</TableHead>
                  <TableHead className="text-xs font-medium text-right">Synced</TableHead>
                  <TableHead className="text-xs font-medium text-right">Skipped</TableHead>
                  <TableHead className="text-xs font-medium text-right">Removed</TableHead>
                  <TableHead className="text-xs font-medium text-right">Feed Total</TableHead>
                  <TableHead className="text-xs font-medium">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {s.sync_history.map((log, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono text-slate-500">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-emerald-700">{log.synced}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-400">{log.skipped}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-400">{log.removed || 0}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-500">{log.total_in_feed || '—'}</TableCell>
                    <TableCell>
                      {log.error ? (
                        <span className="flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3 h-3" /> Error</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle className="w-3 h-3" /> OK</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* CREA Compliance Status */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">CREA Compliance Checklist</h2>
          <div className="space-y-2">
            {[
              { check: 'Powered by REALTOR.ca badge on listing pages', pass: true },
              { check: 'MoreInformationLink pointing to REALTOR.ca', pass: true },
              { check: 'Agent/broker attribution displayed', pass: true },
              { check: 'Hourly data refresh schedule', pass: true },
              { check: 'Session logout after each sync', pass: true },
              { check: 'CREA Analytics events tracked on listing views', pass: true },
              { check: 'TLS v1.2+ for all API connections', pass: true },
              { check: `Photo coverage: ${photoRate}% of listings`, pass: photoRate >= 80 },
              { check: `Neighbourhood coverage: ${coverageCount}/12 areas`, pass: coverageCount >= 8 },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px]">
                {item.pass ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                <span className={item.pass ? 'text-slate-600' : 'text-amber-700'}>{item.check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    </OpsLayout>
  );
}
